import React, { Fragment, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { parseOnlyNumbers } from "../../util/strings";
import EventBusPublisher from "../event-bus-publisher";
import { withRouter } from "react-router-dom";

const Tab = (props) => {
  const {
    tabRef,
    data,
    showDivider = true,
    active = false,
    handleOnClick,
  } = props;
  const titles = data.columnsTitles;
  return (
    <Fragment>
      {showDivider && <span className="divider">|</span>}
      <div
        ref={tabRef}
        tabIndex={"-1"}
        className={`tab ${active ? "active" : ""}`}
        onClick={handleOnClick}
      >
        <div className="full-title">
          {titles.map(({ title, highlighted }, i) => {
            return (
              <span
                key={i}
                className={`title ${highlighted ? "highlighted" : ""}`}
              >
                {title}
              </span>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
};

export function Tabsline(props) {
  const isFixed = props.fixed;
  const pageData = props.pageData;
  const page = props.page;
  const history = props.history;
  const tabsRef = useRef([]);
  const tabslineRef = useRef(null);

  // Add wheel event handler for mouse wheel scrolling
  useEffect(() => {
    const handleWheel = (e) => {
      if (tabslineRef.current) {
        e.preventDefault();
        tabslineRef.current.scrollLeft += e.deltaY;
      }
    };

    const tabslineElement = tabslineRef.current;
    if (tabslineElement) {
      tabslineElement.addEventListener("wheel", handleWheel, {
        passive: false,
      });
    }

    return () => {
      if (tabslineElement) {
        tabslineElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, []);

  // Add drag scrolling functionality
  useEffect(() => {
    let isDown = false;
    let startX;
    let scrollLeft;
    let isDragging = false;

    const handleMouseDown = (e) => {
      isDown = true;
      isDragging = false;
      tabslineRef.current.style.cursor = "grabbing";
      startX = e.pageX - tabslineRef.current.offsetLeft;
      scrollLeft = tabslineRef.current.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      isDragging = false;
      tabslineRef.current.style.cursor = "grab";
    };

    const handleMouseUp = (e) => {
      isDown = false;
      tabslineRef.current.style.cursor = "grab";

      // If we were dragging, prevent the tab click
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
      isDragging = false;
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();

      const x = e.pageX - tabslineRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier

      // If we've moved more than 5 pixels, consider it a drag
      if (Math.abs(walk) > 5) {
        isDragging = true;
      }

      tabslineRef.current.scrollLeft = scrollLeft - walk;
    };

    // Add click handler to prevent tab clicks during drag
    const handleClick = (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const tabslineElement = tabslineRef.current;
    if (tabslineElement) {
      tabslineElement.addEventListener("mousedown", handleMouseDown);
      tabslineElement.addEventListener("mouseleave", handleMouseLeave);
      tabslineElement.addEventListener("mouseup", handleMouseUp);
      tabslineElement.addEventListener("mousemove", handleMouseMove);
      tabslineElement.addEventListener("click", handleClick, true); // Use capture phase
    }

    return () => {
      if (tabslineElement) {
        tabslineElement.removeEventListener("mousedown", handleMouseDown);
        tabslineElement.removeEventListener("mouseleave", handleMouseLeave);
        tabslineElement.removeEventListener("mouseup", handleMouseUp);
        tabslineElement.removeEventListener("mousemove", handleMouseMove);
        tabslineElement.removeEventListener("click", handleClick, true);
      }
    };
  }, []);

  useEffect(() => {
    if (tabsRef.current[page]) {
      tabsRef.current[page].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [page, tabsRef]);

  useEffect(() => {
    const addMessageListener = () => {
      window.addEventListener("message", (event) => {
        const data = event.data || {};
        const type = data.type;
        if (type === "GOTO_NYATC_FRONT_PAGE") {
          history.push("/news/1");
        }
      });
    };
    addMessageListener();
    return () => {
      window.removeEventListener("message", () => {});
    };
  }, []);

  const handleOnClick = (pageTitle) => {
    pageTitle = pageTitle.toLowerCase();
    const pageNumber = pageTitle.includes("page")
      ? parseOnlyNumbers(pageTitle)
      : isNaN(parseInt(pageTitle))
      ? 1
      : parseInt(pageTitle);

    const currentPath = history.location.pathname;
    const isArticlePage = currentPath.includes("/article");
    if ((page === pageNumber && isArticlePage) || page !== pageNumber) {
      if (typeof props.onLoadingStart === "function") {
        props.onLoadingStart();
      }
      history.push(`/news/${pageNumber}`);
    }
  };

  // Show only music tabs (category === "music" or showInBoth)
  const renderMusicTabs = (_pageData) => {
    return _pageData
      .filter((item) => item.category === "music" || item.showInBoth)
      .map((item, index) => {
        const isActive = item.title === `page-${page}`;
        return (
          <Tab
            tabRef={(el) => (tabsRef.current[index] = el)}
            key={"tab-" + index}
            data={item}
            active={isActive}
            showDivider={index > 0}
            handleOnClick={() => handleOnClick(item.title)}
          />
        );
      });
  };

  return (
    <div
      ref={tabslineRef}
      id="page_tabs"
      className={`tabsline line ${isFixed ? "fixed" : ""}`}
    >
      {renderMusicTabs(pageData, page)}
    </div>
  );
}

Tabsline.contextTypes = {
  pageData: PropTypes.object,
  fixed: PropTypes.bool,
  page: PropTypes.number,
  history: PropTypes.object,
};

const mapStateToProps = (state) => {
  let pageData = state?.contrarianPage?.all || [];
  pageData = pageData.sort((a, b) => a.order - b.order);
  return {
    pageData: pageData,
  };
};

export default withRouter(connect(mapStateToProps)(Tabsline));
